import { KeysMatching } from "../../../util";

// These strings are saved in persisted data and can't be changed or you'll
// break saved filters.
export const SORT_FIELD_NAMES = [
  "company_name",
  "broker_name",
  "eff_date",
  "status",
  "tiv",
  "creation_date",
] as const;
export type SORT_FIELD_NAME = (typeof SORT_FIELD_NAMES)[number];

export type Filter = {
  setFields: (keyof Filter)[];
  selectedAssigneeUserIds: string[];
  selectedHandlerUserIds: string[];
  selectedStatuses: string[];
  selectedAppetites: string[];
  initialSortField: SORT_FIELD_NAME;
  initialSortOrder: "asc" | "desc";
};

export const DEFAULT_FILTER: Filter = {
  setFields: [],
  selectedAssigneeUserIds: [],
  selectedHandlerUserIds: [],
  selectedStatuses: [],
  selectedAppetites: [],
  initialSortField: "creation_date",
  initialSortOrder: "desc",
};

const KEY_MAPPING: { [key in keyof Filter]-?: string } = {
  setFields: "fields",
  selectedAssigneeUserIds: "assign",
  selectedHandlerUserIds: "handler",
  selectedStatuses: "st",
  selectedAppetites: "ap",
  initialSortField: "sf",
  initialSortOrder: "so",
};

const REVERSE_KEY_MAPPING = Object.fromEntries(
  Object.entries(KEY_MAPPING).map(([a, b]) => [b, a])
) as { [key: string]: keyof Filter };

/* Returns a URL-safe serialization of a filter, suitable for use as a URL "search" */
export const serializeFilter = (filter: Filter): string => {
  const params = new URLSearchParams();
  for (const [untypedK, urlKey] of Object.entries(KEY_MAPPING)) {
    const k = untypedK as keyof Filter;

    const value = filter[k];
    if (k === "setFields" && Array.isArray(value)) {
      params.append(
        KEY_MAPPING.setFields,
        value
          .map((v) => KEY_MAPPING[v as keyof Filter])
          .filter((v) => !!v)
          .join(".")
      );
    } else if (filter["setFields"].includes(k)) {
      if (typeof value === "string") {
        params.append(urlKey, value);
      } else {
        for (const v of value) {
          if (v) {
            params.append(urlKey, v);
          }
        }
      }
    }
  }

  return params.toString();
};

/* Parse a filter from a URL search */
export const deserializeFilter = (search: string): Filter => {
  const params = new URLSearchParams(search);

  const getOrDefault = (k: KeysMatching<Filter, string>): string =>
    params.get(KEY_MAPPING[k]) ?? DEFAULT_FILTER[k];

  const setFields = (params.get(KEY_MAPPING["setFields"])?.split(".") ?? [])
    .map((v) => REVERSE_KEY_MAPPING[v])
    .filter((v) => !!v);
  const selectedAssigneeUserIds = params.getAll(
    KEY_MAPPING["selectedAssigneeUserIds"]
  );
  const selectedHandlerUserIds = params.getAll(
    KEY_MAPPING["selectedHandlerUserIds"]
  );
  const selectedStatuses = params.getAll(KEY_MAPPING["selectedStatuses"]);
  const selectedAppetites = params.getAll(KEY_MAPPING["selectedAppetites"]);
  const initialSortField = getOrDefault("initialSortField") as SORT_FIELD_NAME;
  const initialSortOrderString = getOrDefault("initialSortOrder");
  const initialSortOrder =
    initialSortOrderString === "asc" || initialSortOrderString == "desc"
      ? initialSortOrderString
      : DEFAULT_FILTER.initialSortOrder;

  return {
    setFields,
    selectedAssigneeUserIds,
    selectedHandlerUserIds,
    selectedStatuses,
    selectedAppetites,
    initialSortField,
    initialSortOrder,
  };
};
